/**
 * Keeping the open level's work, so a reload does not throw it away.
 *
 * The workspace lives in the emulator, and the emulator lives in the page, so
 * before this every reload — a stalled machine, a closed laptop, a deploy, a
 * click on another level — sent a learner back to the level's starting
 * point. This watches the one open level and copies the learner's changes
 * out of the guest into this browser (lib/worksave.ts), and puts them back
 * when the level opens again. packages/vm/src/worksave.ts is what a copy is.
 *
 * ## When a copy is taken
 *
 * Only after the learner has done something — a command at the prompt, a
 * save in the editor, a reset — and then once they have paused for
 * `QUIET_MS`, or every `BUSY_MS` while they keep going, or at once when the
 * tab is hidden, or before another level opens. A copy is an exec on the
 * same line a Check uses, so it is never taken while nothing has changed,
 * and a page left open overnight costs nothing.
 *
 * Nothing can be done at `pagehide`: an exec into the guest is a round trip
 * the page will not live to see answered. So a reload loses at most what was
 * done in the last few seconds, and the hidden-tab copy covers the common
 * way a tab is closed, which is to switch away from it first.
 *
 * ## When a copy is not restored
 *
 * - The level's seed changed (`Mission.seedPrint`): the copy is a difference
 *   from a starting workspace that no longer exists. The level opens fresh
 *   and the learner is told why.
 * - The salt changed: a new attempt at a checkpoint, or another account in
 *   this browser. Opens fresh, silently; the old attempt was already over.
 * - The learner pressed Reset, or passed the level cleanly: the copy is
 *   dropped, because both mean "start this level from nothing next time".
 *
 * ## What a restore carries
 *
 * The plausibility check (runtime.ts, `check`) measures a level from when it
 * first opened, compares the repository with the one seeded, and counts the
 * commands typed. A restore that forgot those would flag honest work: ten
 * minutes of it before a reload, and a pass ten seconds after. So each copy
 * carries the first-open time, the seeded fingerprint and the counts, and a
 * restore hands them back. They can only make a level look slower and more
 * worked-at than a fresh open would, never faster.
 */
import type { GitEvidence, Mission, Session } from '@engineer-os/engine';
import { captureWork, type SavedWork, type WorkRestorer } from '@engineer-os/vm';
import { forgetKept, keep, readKept, type KeptLevel } from '../lib/worksave';

/** How long after the last command or save a copy is taken. */
const QUIET_MS = 10_000;
/** The longest a learner who never pauses goes between copies. */
const BUSY_MS = 60_000;
const TICK_MS = 2_500;
/** A level change waits at most this long for the copy of the level being left. */
const FLUSH_MS = 15_000;

/** What opening a level found in this browser, for the screen to say. */
export type Resumed = { kind: 'restored'; savedAt: number } | { kind: 'changed' } | null;

/** What a restored level hands back to the runtime's plausibility check. */
export interface Carried {
  openedAt: number;
  before: GitEvidence | null;
  startHead: string | null;
  commands: number;
  saves: number;
}

interface Watched {
  session: Session;
  openedAt: number;
  before: GitEvidence | null;
  carried: { commands: number; saves: number };
  /** Activity events at the last copy, and when the count last moved. */
  copied: number;
  seen: number;
  lastActivity: number;
  lastCopy: number;
}

/** The events that change files. Checks, hints and the mentor do not. */
function activity(session: Session): number {
  return session.telemetry.filter((e) => e.type === 'command' || e.type === 'save' || e.type === 'reset').length;
}

function counted(session: Session, type: 'command' | 'save'): number {
  return session.telemetry.filter((e) => e.type === type).length;
}

export class WorkKeeper implements WorkRestorer {
  /** The copy armed for the next open of its level, and what that open did with it. */
  private armed: { missionId: string; kept: KeptLevel } | null = null;
  private outcome: { missionId: string; kept: KeptLevel; restored: boolean } | null = null;
  /** The level whose copy `prepare` dropped because its seed changed, for `opened` to report. */
  private changed: string | null = null;
  private watched: Watched | null = null;
  /** Moves on every reset, pass and level change, so a copy that was in flight across one is discarded. */
  private generation = 0;
  private inFlight: Promise<void> | null = null;

  constructor(private readonly saltFor: (missionId: string) => Promise<string | null>) {
    const timer = setInterval(() => this.tick(false), TICK_MS);
    // Under Node (the tests) a timer that must not hold the process open.
    (timer as { unref?: () => void }).unref?.();
    try {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.tick(true);
      });
    } catch {
      /* no document: nothing to hide */
    }
  }

  /* --- The seeder's side (WorkRestorer) ----------------------------------- */

  take(missionId: string): SavedWork | null {
    const armed = this.armed;
    this.armed = null;
    if (!armed || armed.missionId !== missionId) return null;
    // Recorded as a failure until the seeder says otherwise, so a seed that
    // throws after taking it is not mistaken for a restore.
    this.outcome = { missionId, kept: armed.kept, restored: false };
    return armed.kept.work;
  }

  settled(missionId: string, restored: boolean): void {
    if (this.outcome?.missionId === missionId) this.outcome.restored = restored;
    // A copy that would not go back will not go back next time either.
    if (!restored) void forgetKept(missionId);
  }

  /* --- The runtime's side ------------------------------------------------- */

  /**
   * Before a level opens: arm its kept work if it can go back, and say what
   * was found. Never throws — at worst the level opens fresh.
   */
  async prepare(mission: Mission): Promise<void> {
    this.armed = null;
    this.outcome = null;
    this.changed = null;
    const kept = await readKept(mission.id);
    // No fingerprint is a build this change predates: nothing can be proved
    // about the base, so nothing is restored, and nothing is thrown away.
    if (!kept || !mission.seedPrint) return;
    if (kept.seedPrint !== mission.seedPrint) {
      await forgetKept(mission.id);
      this.changed = mission.id;
      return;
    }
    let salt: string | null;
    try {
      salt = await this.saltFor(mission.id);
    } catch {
      return; // the seeder will refuse this level for the same reason, and say so
    }
    if (salt !== kept.salt) {
      await forgetKept(mission.id);
      return;
    }
    this.armed = { missionId: mission.id, kept };
  }

  /** After the open: what came back, and what it carries. */
  opened(missionId: string): { resumed: Resumed; carried: Carried | null } {
    const outcome = this.outcome?.missionId === missionId ? this.outcome : null;
    const changed = this.changed === missionId;
    this.outcome = null;
    this.armed = null;
    this.changed = null;
    if (changed) return { resumed: { kind: 'changed' }, carried: null };
    if (!outcome?.restored) return { resumed: null, carried: null };
    const k = outcome.kept;
    return {
      resumed: { kind: 'restored', savedAt: k.savedAt },
      carried: { openedAt: k.openedAt, before: k.before, startHead: k.startHead, commands: k.commands, saves: k.saves },
    };
  }

  /** Start watching the level that just opened. */
  watch(session: Session, openedAt: number, before: GitEvidence | null, carried: Carried | null): void {
    this.generation++;
    const now = Date.now();
    const seen = activity(session);
    this.watched = {
      session,
      openedAt,
      before,
      carried: { commands: carried?.commands ?? 0, saves: carried?.saves ?? 0 },
      copied: seen,
      seen,
      lastActivity: now,
      lastCopy: now,
    };
  }

  /**
   * Copy the watched level now if anything changed, and wait for it — the
   * level being left, before the next one wipes the workspace. Bounded: a
   * slow guest delays a level change by at most `FLUSH_MS`, never blocks it.
   */
  async flush(): Promise<void> {
    const done = (async () => {
      if (this.inFlight) await this.inFlight;
      const w = this.watched;
      if (w && activity(w.session) !== w.copied) await this.copy(w);
    })();
    let deadline: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([done, new Promise((resolve) => (deadline = setTimeout(resolve, FLUSH_MS)))]);
    clearTimeout(deadline);
  }

  /** Stop watching: the level is about to be replaced. */
  stop(): void {
    this.generation++;
    this.watched = null;
  }

  /** Drop the copy — a reset. The level stays watched, and new work is copied again. */
  async forget(missionId: string): Promise<void> {
    this.generation++;
    await forgetKept(missionId);
    const w = this.watched;
    if (w && w.session.mission.id === missionId) w.copied = activity(w.session);
  }

  /** Drop the copy and stop watching — a clean pass. The next open of this level is a fresh one. */
  async finish(missionId: string): Promise<void> {
    if (this.watched?.session.mission.id === missionId) this.stop();
    else this.generation++;
    await forgetKept(missionId);
  }

  /* --- Copying ------------------------------------------------------------ */

  private tick(now: boolean): void {
    const w = this.watched;
    if (!w || this.inFlight) return;
    const t = Date.now();
    const n = activity(w.session);
    if (n !== w.seen) {
      w.seen = n;
      w.lastActivity = t;
    }
    if (n === w.copied) return;
    if (now || t - w.lastActivity >= QUIET_MS || t - w.lastCopy >= BUSY_MS) void this.copy(w);
  }

  private copy(w: Watched): Promise<void> {
    if (this.inFlight) return this.inFlight;
    const generation = this.generation;
    const run = (async () => {
      const session = w.session;
      const mission = session.mission;
      if (!mission.seedPrint) return;
      const events = activity(session);
      try {
        const salt = await this.saltFor(mission.id).catch(() => null);
        // The workspace as it is now: a reset replaces the object on the session.
        const work = await captureWork(session.workspace, mission.id);
        // A reset, a pass or another level landed while this was in the guest:
        // what came out describes a workspace that is no longer there.
        if (generation !== this.generation || this.watched !== w) return;
        if (work === 'not-seeded') return;
        w.copied = events;
        w.lastCopy = Date.now();
        if (work === 'too-large') {
          // Keeping the last copy that fitted would bring back an older state
          // than the one left, which is worse than bringing back none.
          await forgetKept(mission.id);
          return;
        }
        await keep({
          missionId: mission.id,
          seedPrint: mission.seedPrint,
          salt,
          savedAt: Date.now(),
          openedAt: w.openedAt,
          before: w.before,
          startHead: session.startHead ?? null,
          commands: w.carried.commands + counted(session, 'command'),
          saves: w.carried.saves + counted(session, 'save'),
          work,
        });
      } catch (err) {
        // Not fatal and not retried at once: the next activity tries again.
        w.lastCopy = Date.now();
        console.warn('worksave: could not keep this level', err instanceof Error ? err.message : err);
      }
    })().finally(() => {
      this.inFlight = null;
    });
    this.inFlight = run;
    return run;
  }
}
